/** Materiais e classificações de um elemento: ler e mudar.
 *
 *  Material: só se troca um IfcMaterial simples por outro. Um elemento
 *  com material em camadas (IfcMaterialLayerSetUsage), perfis ou
 *  constituintes tem uma estrutura que uma célula de Excel não descreve;
 *  trocá-la por um material simples apagava-a. Recusa-se e diz-se porquê.
 *  O material com o nome pedido reaproveita-se se existir (o primeiro do
 *  ficheiro, quando há vários com o mesmo nome, e diz-se qual); senão
 *  cria-se.
 *
 *  Classificação: cada elemento tem, por sistema (Uniclass, OmniClass,
 *  SECClasS...), uma referência. Pôr um código noutro sistema não mexe
 *  nos outros; pôr outro código no mesmo sistema substitui o anterior.
 *  O sistema e a referência reaproveitam-se se existirem.
 *
 *  Posições dos atributos (IFC2X3 e IFC4):
 *  - IfcRelAssociates*: GlobalId, OwnerHistory, Name, Description,
 *    RelatedObjects (4), Relating* (5);
 *  - IfcMaterial: Name (0) [, Description, Category no IFC4];
 *  - IfcClassificationReference: Location, Identification/ItemReference
 *    (1), Name (2), ReferencedSource (3) [, Description, Sort no IFC4];
 *  - IfcClassification: Source, Edition, EditionDate, Name (3) [, ...]. */

import type { ModeloStep } from '@viewer/lib/step/modeloStep'
import { argTexto, lerArgTexto, lerListaDeRefs, lerRef } from '@viewer/lib/step/textoStep'

export interface IndiceDeAssociacoes {
  materiais: Map<number, number[]>
  classificacoes: Map<number, number[]>
}

export interface Contexto {
  m: ModeloStep
  novoGuid: () => string
  donoDaHistoria: string
}

function juntar(mapa: Map<number, number[]>, chave: number, v: number) {
  const a = mapa.get(chave)
  if (a) a.push(v)
  else mapa.set(chave, [v])
}

export function construirIndiceDeAssociacoes(m: ModeloStep): IndiceDeAssociacoes {
  const materiais = new Map<number, number[]>()
  const classificacoes = new Map<number, number[]>()
  for (const rel of m.idsDaClasse('IFCRELASSOCIATESMATERIAL')) {
    for (const o of lerListaDeRefs(m.args(rel)[4])) juntar(materiais, o, rel)
  }
  for (const rel of m.idsDaClasse('IFCRELASSOCIATESCLASSIFICATION')) {
    for (const o of lerListaDeRefs(m.args(rel)[4])) juntar(classificacoes, o, rel)
  }
  return { materiais, classificacoes }
}

// ---------------------------------------------------------------------
// Ler
// ---------------------------------------------------------------------

export interface MaterialDoElemento {
  /** Texto para mostrar: o nome, ou a forma do material composto. */
  texto: string | null
  /** true quando é um IfcMaterial simples (o único caso que se troca). */
  simples: boolean
  id: number | null
}

const DESCRICAO_COMPOSTO: Record<string, string> = {
  IFCMATERIALLAYERSETUSAGE: 'material em camadas',
  IFCMATERIALLAYERSET: 'conjunto de camadas',
  IFCMATERIALPROFILESETUSAGE: 'material por perfil',
  IFCMATERIALPROFILESET: 'conjunto de perfis',
  IFCMATERIALCONSTITUENTSET: 'conjunto de constituintes',
  IFCMATERIALLIST: 'lista de materiais',
}

export function materialDe(m: ModeloStep, ix: IndiceDeAssociacoes, obj: number): MaterialDoElemento {
  const rels = (ix.materiais.get(obj) ?? []).filter((r) => m.existe(r))
  if (rels.length === 0) return { texto: null, simples: true, id: null }
  const ids = rels.map((r) => lerRef(m.args(r)[5])).filter((x): x is number => x !== null)
  if (ids.length === 1 && m.classe(ids[0]) === 'IFCMATERIAL') {
    return { texto: lerArgTexto(m.args(ids[0])[0]), simples: true, id: ids[0] }
  }
  const descr = ids.map((id) => `${DESCRICAO_COMPOSTO[m.classe(id) ?? ''] ?? m.classe(id)} (#${id})`).join(', ')
  return { texto: descr, simples: false, id: ids[0] ?? null }
}

export interface ReferenciaDeClassificacao {
  sistema: string
  codigo: string | null
  nome: string | null
  referencia: number
  rel: number
}

/** O IfcClassification de uma referência, subindo pela cadeia de
 *  ReferencedSource (no IFC4 uma referência pode apontar para outra). */
function sistemaDe(m: ModeloStep, id: number): number | null {
  let actual: number | null = id
  for (let i = 0; i < 20 && actual !== null; i++) {
    const c = m.classe(actual)
    if (c === 'IFCCLASSIFICATION') return actual
    if (c !== 'IFCCLASSIFICATIONREFERENCE') return null
    actual = lerRef(m.args(actual)[3])
  }
  return null
}

export function classificacoesDe(m: ModeloStep, ix: IndiceDeAssociacoes, obj: number): ReferenciaDeClassificacao[] {
  const out: ReferenciaDeClassificacao[] = []
  for (const rel of ix.classificacoes.get(obj) ?? []) {
    if (!m.existe(rel)) continue
    const ref = lerRef(m.args(rel)[5])
    if (ref === null) continue
    const sis = sistemaDe(m, ref)
    const sistema = sis !== null ? lerArgTexto(m.args(sis)[3]) ?? '(sem nome)' : '(sem sistema)'
    if (m.classe(ref) === 'IFCCLASSIFICATIONREFERENCE') {
      const a = m.args(ref)
      out.push({ sistema, codigo: lerArgTexto(a[1]), nome: lerArgTexto(a[2]), referencia: ref, rel })
    } else {
      out.push({ sistema, codigo: null, nome: null, referencia: ref, rel })
    }
  }
  return out
}

// ---------------------------------------------------------------------
// Mudar
// ---------------------------------------------------------------------

export interface Resultado {
  erro?: string
  mudou: boolean
  criados: number
  avisos: string[]
}

/** Tira o objecto de uma relação de associação; a relação que fica vazia apaga-se. */
function tirarDaRelacao(m: ModeloStep, mapa: Map<number, number[]>, obj: number, rel: number) {
  const objs = lerListaDeRefs(m.args(rel)[4])
  if (objs.length <= 1) m.apagar([rel])
  else m.definirArg(rel, 4, `(${objs.filter((o) => o !== obj).map((o) => `#${o}`).join(',')})`)
  mapa.set(obj, (mapa.get(obj) ?? []).filter((r) => r !== rel))
}

/** Põe o objecto numa relação que já aponta para `alvo`, ou cria uma. */
function porNaRelacao(
  ctx: Contexto,
  mapa: Map<number, number[]>,
  classeDaRelacao: string,
  obj: number,
  alvo: number,
): number {
  const { m } = ctx
  const existente = m.idsDaClasse(classeDaRelacao).find((r) => lerRef(m.args(r)[5]) === alvo)
  if (existente !== undefined) {
    const objs = lerListaDeRefs(m.args(existente)[4])
    m.definirArg(existente, 4, `(${[...objs, obj].map((o) => `#${o}`).join(',')})`)
    juntar(mapa, obj, existente)
    return 0
  }
  const rel = m.criar(classeDaRelacao, [argTexto(ctx.novoGuid()), ctx.donoDaHistoria, '$', '$', `(#${obj})`, `#${alvo}`])
  juntar(mapa, obj, rel)
  return 1
}

export function associarMaterial(ctx: Contexto, ix: IndiceDeAssociacoes, obj: number, nome: string): Resultado {
  const { m } = ctx
  const avisos: string[] = []
  const pedido = nome.trim()
  if (!pedido) return { erro: 'Sem nome de material.', mudou: false, criados: 0, avisos }
  const actual = materialDe(m, ix, obj)
  if (!actual.simples) {
    return {
      erro: `O elemento tem ${actual.texto}: uma estrutura que uma célula não descreve. Trocá-la por um material simples apagava-a; muda-se no programa de autoria.`,
      mudou: false,
      criados: 0,
      avisos,
    }
  }
  if (actual.texto === pedido) return { mudou: false, criados: 0, avisos }
  // O material com esse nome: o primeiro do ficheiro, se houver vários.
  const comNome = m.idsDaClasse('IFCMATERIAL').filter((id) => lerArgTexto(m.args(id)[0]) === pedido)
  let criados = 0
  let material: number
  if (comNome.length > 0) {
    material = comNome[0]
    if (comNome.length > 1) {
      avisos.push(`Há ${comNome.length} IfcMaterial chamados "${pedido}" (${comNome.map((i) => `#${i}`).join(', ')}); usa-se o primeiro do ficheiro, #${material}.`)
    }
  } else {
    material = m.criar('IFCMATERIAL', m.esquema() === 'IFC2X3' ? [argTexto(pedido)] : [argTexto(pedido), '$', '$'])
    criados++
  }
  for (const rel of [...(ix.materiais.get(obj) ?? [])]) if (m.existe(rel)) tirarDaRelacao(m, ix.materiais, obj, rel)
  criados += porNaRelacao(ctx, ix.materiais, 'IFCRELASSOCIATESMATERIAL', obj, material)
  return { mudou: true, criados, avisos }
}

export function classificar(
  ctx: Contexto,
  ix: IndiceDeAssociacoes,
  obj: number,
  sistema: string,
  codigo: string,
  nome?: string,
): Resultado {
  const { m } = ctx
  const avisos: string[] = []
  const sis = sistema.trim()
  const cod = codigo.trim()
  if (!sis || !cod) return { erro: 'Falta o sistema ou o código.', mudou: false, criados: 0, avisos }
  const actuais = classificacoesDe(m, ix, obj).filter((c) => c.sistema === sis)
  if (actuais.some((c) => c.codigo === cod) && actuais.length === 1) return { mudou: false, criados: 0, avisos }
  let criados = 0
  // O sistema.
  let classificacao = m.idsDaClasse('IFCCLASSIFICATION').find((id) => lerArgTexto(m.args(id)[3]) === sis)
  if (classificacao === undefined) {
    classificacao = m.criar(
      'IFCCLASSIFICATION',
      m.esquema() === 'IFC2X3'
        ? ["''", "''", '$', argTexto(sis)]
        : ['$', '$', '$', argTexto(sis), '$', '$', '$'],
    )
    criados++
    avisos.push(`O sistema "${sis}" não existia no ficheiro: foi criado (#${classificacao}), sem fonte nem edição.`)
  }
  // A referência, dentro desse sistema.
  let referencia = m
    .idsDaClasse('IFCCLASSIFICATIONREFERENCE')
    .find((id) => lerArgTexto(m.args(id)[1]) === cod && sistemaDe(m, id) === classificacao)
  if (referencia === undefined) {
    referencia = m.criar(
      'IFCCLASSIFICATIONREFERENCE',
      m.esquema() === 'IFC2X3'
        ? ['$', argTexto(cod), nome ? argTexto(nome) : '$', `#${classificacao}`]
        : ['$', argTexto(cod), nome ? argTexto(nome) : '$', `#${classificacao}`, '$', '$'],
    )
    criados++
  }
  for (const c of actuais) tirarDaRelacao(m, ix.classificacoes, obj, c.rel)
  criados += porNaRelacao(ctx, ix.classificacoes, 'IFCRELASSOCIATESCLASSIFICATION', obj, referencia)
  return { mudou: true, criados, avisos }
}
